import React, { FC, ComponentType, useCallback, ChangeEvent, FocusEvent, ForwardedRef } from 'react';
import { ElementWithValue } from 'ia-react-core/~/interfaces/ElementWithValue';
import { FixedKeyboardEvent } from 'ia-react-core/~/interfaces/FixedKeyboardEvent';

import excludePropsByComponent from '../../../utils/components/_excludePropsByComponent';
import Control, { ControlProps, ControlRenderProps } from '../Control';
import Textarea from '../Textarea';
import { TextareaProps } from '../Textarea/Textarea';

export type TextareaControlValidationProps = {
  required?: boolean;
  /** Defines the "minLength" validator. */
  minLength?: number;
  /** Maximum number of characters */
  maxLength?: number;
  recommended?: number;
};

export interface TextareaControlProps extends
    Omit<TextareaProps, 'defaultValue' | 'name' | 'value'>,
    Omit<ControlProps<string, string, HTMLTextAreaElement, TextareaControlValidationProps>, 'children'>,
    TextareaControlValidationProps {
  /** Trims spaces from both end of the input. */
  trim?: boolean;
  /** Element type to use as the input. By default [TextInput](/Core/Components/Form/TextInput) */
  Input?: ComponentType<any>;
  ref?: ForwardedRef<HTMLTextAreaElement>;
}

interface TextareaControlInternalProps extends Omit<ControlRenderProps<string, HTMLTextAreaElement>, 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown' | 'id'> {
  onChangeCallback: ControlRenderProps<string, HTMLTextAreaElement>['onChange'];
  onBlurCallback: ControlRenderProps<string, HTMLTextAreaElement>['onBlur'];
  onKeyDownCallback: ControlRenderProps<string, HTMLTextAreaElement>['onKeyDown'];
}

export interface TextareaControlComposedProps extends Omit<TextareaControlProps, 'isTouched' | 'isValid'>, TextareaControlInternalProps {

}

interface TextareaControlStatics {
  labeledControl: boolean;
}

export const TextareaControlDefaultProps = {
  trim: true,
  Input: Textarea,
  maxLength: 5000,
};

const TextareaControl: FC<TextareaControlComposedProps> & TextareaControlStatics = ({
  id,
  name,
  trim,
  required,
  readOnly,
  textOnly,
  minLength,
  maxLength,
  valueToStateFormatters,
  Input,
  isValid,
  hasWarning,
  hasCaution,
  hasInfo,
  isTouched,
  errorMessagesId,
  value,
  inputRef,
  onChange,
  onBlur,
  onKeyDown,
  registerControlGroup,
  unregisterControlGroup,
  onChangeCallback,
  onBlurCallback,
  onKeyDownCallback,
  ...props
}) => {
  const handleChange = useCallback((event: ChangeEvent<ElementWithValue<string>>) => {
    onChangeCallback(event);
    if (onChange) onChange(event as ChangeEvent<HTMLTextAreaElement>);
  }, [onChange, onChangeCallback]);

  const handleBlur = useCallback((event: FocusEvent<ElementWithValue<string>>) => {
    onBlurCallback(event);
    if (onBlur) onBlur(event as FocusEvent<HTMLTextAreaElement>);
  }, [onBlur, onBlurCallback]);

  const handleKeydown = useCallback((event: FixedKeyboardEvent) => {
    onKeyDownCallback(event);
    if (onKeyDown) onKeyDown(event);
  }, [onKeyDownCallback, onKeyDown]);

  return (

    <Input
      id={id}
      data-control
      data-testid="TextareaControl"
      aria-invalid={Boolean(!isValid && isTouched)}
      aria-errormessage={errorMessagesId}
      data-control-nesting="1"
      readOnly={readOnly}
      textOnly={textOnly}
      {...excludePropsByComponent(props, Control)}
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeydown}
      isValid={isValid}
      hasWarning={hasWarning}
      hasCaution={hasCaution}
      hasInfo={hasInfo}
      isTouched={isTouched}
      inputRef={inputRef}
    />
  );
};

TextareaControl.labeledControl = true;
TextareaControl.defaultProps = TextareaControlDefaultProps;

export default TextareaControl;
